import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type SortingState,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EmptyState } from '@/components/tables/EmptyState';

export interface ServerSidePagination {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

export interface ServerSideSorting {
  sort: string | null;
  order: 'asc' | 'desc' | null;
  onSortChange: (sort: string | null, order: 'asc' | 'desc' | null) => void;
}

export interface QueueTableProps<T> {
  columns: ColumnDef<T, unknown>[];
  data: T[];
  total?: number;
  loading?: boolean;
  onRowClick?: (row: T) => void;
  selectedRowId?: string | null;
  serverSidePagination?: ServerSidePagination;
  serverSideSorting?: ServerSideSorting;
  emptyState?: ReactNode;
  /** Stable row id accessor; defaults to internal index. */
  getRowId?: (row: T) => string;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [data-stop-row-click]';

function QueueCellContent({
  children,
  primitiveTitle,
}: {
  children: ReactNode;
  primitiveTitle?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [renderedTitle, setRenderedTitle] = useState<string | undefined>(primitiveTitle);

  useEffect(() => {
    const text = ref.current?.textContent?.trim();
    setRenderedTitle(text || primitiveTitle);
  }, [children, primitiveTitle]);

  return (
    <div ref={ref} className="truncate whitespace-nowrap" title={renderedTitle}>
      {children}
    </div>
  );
}

export function QueueTable<T>({
  columns,
  data,
  total,
  loading,
  onRowClick,
  selectedRowId,
  serverSidePagination,
  serverSideSorting,
  emptyState,
  getRowId,
  className,
}: QueueTableProps<T>) {
  const [clientSorting, setClientSorting] = useState<SortingState>([]);
  const sorting: SortingState = serverSideSorting?.sort
    ? [{ id: serverSideSorting.sort, desc: serverSideSorting.order === 'desc' }]
    : clientSorting;

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    if (serverSideSorting) {
      const first = next[0];
      serverSideSorting.onSortChange(
        first?.id ?? null,
        first ? (first.desc ? 'desc' : 'asc') : null,
      );
      return;
    }
    setClientSorting(next);
  };

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE_OPTIONS[0],
      },
    },
    state: { sorting },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: serverSideSorting ? undefined : getSortedRowModel(),
    getPaginationRowModel: serverSidePagination ? undefined : getPaginationRowModel(),
    manualPagination: !!serverSidePagination,
    manualSorting: !!serverSideSorting,
    pageCount: serverSidePagination
      ? Math.max(1, Math.ceil((total ?? 0) / serverSidePagination.pageSize))
      : undefined,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
  });

  const showingEmpty = !loading && data.length === 0;
  const currentPage = serverSidePagination?.page ?? table.getState().pagination.pageIndex + 1;
  const pageSize = serverSidePagination?.pageSize ?? table.getState().pagination.pageSize;
  const pageCount = serverSidePagination
    ? Math.max(1, Math.ceil((total ?? 0) / pageSize))
    : table.getPageCount();

  const goToPage = (page: number) => {
    if (serverSidePagination) {
      serverSidePagination.onPageChange(page);
    } else {
      table.setPageIndex(page - 1);
    }
  };

  const setPageSize = (size: number) => {
    if (serverSidePagination?.onPageSizeChange) {
      serverSidePagination.onPageSizeChange(size);
    } else if (!serverSidePagination) {
      table.setPageSize(size);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onRowClick) return;
    const target = e.target as HTMLElement;
    if (target.closest(INTERACTIVE_SELECTOR)) return;

    const visibleRows = table.getRowModel().rows;
    if (visibleRows.length === 0) return;
    const currentIndex = selectedRowId
      ? visibleRows.findIndex((row) => row.id === selectedRowId)
      : -1;

    if (e.key === 'Enter' || e.key === ' ') {
      const row = visibleRows[currentIndex >= 0 ? currentIndex : 0];
      if (!row) return;
      e.preventDefault();
      onRowClick(row.original);
      return;
    }

    let nextIndex: number | null = null;
    if (e.key === 'ArrowDown') {
      nextIndex =
        currentIndex < 0 ? 0 : Math.min(visibleRows.length - 1, currentIndex + 1);
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex < 0 ? 0 : Math.max(0, currentIndex - 1);
    } else if (e.key === 'Home') {
      nextIndex = 0;
    } else if (e.key === 'End') {
      nextIndex = visibleRows.length - 1;
    }

    if (nextIndex === null) return;
    e.preventDefault();
    onRowClick(visibleRows[nextIndex].original);
  };

  return (
    <div className={cn('flex flex-col gap-12', className)}>
      <div
        role="region"
        aria-label="Queue table"
        tabIndex={onRowClick ? 0 : undefined}
        onKeyDown={handleKeyDown}
        className="overflow-x-auto rounded-lg border border-neutral-200 bg-neutral-0 focus:outline-none focus:ring-2 focus:ring-primary-500/40"
      >
        <table className="w-full min-w-[920px] border-collapse text-sm">
          <thead className="bg-neutral-100">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => {
                  const sortable = h.column.getCanSort();
                  const sorted = h.column.getIsSorted();
                  return (
                    <th
                      key={h.id}
                      scope="col"
                      className={cn(
                        'border-b border-neutral-200 px-8 py-6 text-left text-xs font-semibold uppercase tracking-wide text-neutral-600',
                        sortable && 'cursor-pointer select-none',
                      )}
                      onClick={sortable ? h.column.getToggleSortingHandler() : undefined}
                    >
                      <span className="inline-flex items-center gap-6">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                        {sortable &&
                          (sorted === 'asc' ? (
                            <ChevronUp className="h-12 w-12 text-neutral-700" />
                          ) : sorted === 'desc' ? (
                            <ChevronDown className="h-12 w-12 text-neutral-700" />
                          ) : (
                            <ChevronsUpDown className="h-12 w-12 text-neutral-400" />
                          ))}
                      </span>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="p-32 text-center">
                  <span className="inline-flex items-center gap-8 text-sm text-neutral-500">
                    <Loader2 className="h-16 w-16 animate-spin" /> Loading...
                  </span>
                </td>
              </tr>
            ) : showingEmpty ? (
              <tr>
                <td colSpan={columns.length} className="p-0">
                  <div className="p-24">
                    {emptyState ?? (
                      <EmptyState
                        title="Nothing here yet"
                        description="When new requests arrive they'll show up in this queue."
                      />
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row, idx) => {
                const isSelected = selectedRowId != null && row.id === selectedRowId;
                return (
                <tr
                  key={row.id}
                  aria-selected={isSelected}
                  onClick={(e) => {
                    if (!onRowClick) return;
                    const target = e.target as HTMLElement;
                    if (target.closest(INTERACTIVE_SELECTOR)) return;
                    onRowClick(row.original);
                  }}
                  className={cn(
                    'border-b border-neutral-200 last:border-0 transition-colors',
                    idx % 2 === 1 && !isSelected && 'bg-neutral-50',
                    onRowClick && 'cursor-pointer hover:bg-primary-500/5',
                    isSelected &&
                      'bg-primary-500/10 shadow-[inset_3px_0_0_theme(colors.primary.500)]',
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const value = cell.getValue();
                    const title =
                      typeof value === 'string' || typeof value === 'number'
                        ? String(value)
                        : undefined;
                    return (
                      <td
                        key={cell.id}
                        className="overflow-hidden px-8 py-6 text-neutral-800"
                      >
                        <QueueCellContent primitiveTitle={title}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </QueueCellContent>
                      </td>
                    );
                  })}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {!showingEmpty && !loading && (
        <div className="flex flex-wrap items-center justify-between gap-12 px-4">
          <div className="flex items-center gap-8 text-sm text-neutral-600">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-9 rounded-md border border-neutral-200 bg-neutral-0 px-8 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              {PAGE_SIZE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {typeof total === 'number' && (
              <span className="text-neutral-500">{total.toLocaleString()} total</span>
            )}
          </div>
          <div className="flex items-center gap-6">
            <button
              type="button"
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="h-9 rounded-md border border-neutral-200 bg-neutral-0 px-12 text-sm text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="px-8 text-sm text-neutral-600">
              Page {currentPage} of {pageCount}
            </span>
            <button
              type="button"
              onClick={() => goToPage(Math.min(pageCount, currentPage + 1))}
              disabled={currentPage >= pageCount}
              className="h-9 rounded-md border border-neutral-200 bg-neutral-0 px-12 text-sm text-neutral-700 hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
